import { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import { colord } from "colord";

type HeaderDetailTooltipProps = {
  children?: React.ReactNode;
  tooltip?: React.ReactNode;
  placement?:
    | "bottom-end"
    | "bottom-start"
    | "bottom"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
  disableTooltip?: boolean;
  sx?: SxProps;
  /**
   * Hover still previews; a click keeps the tooltip open until its Close
   * button (or Escape) is used, so the user can move the mouse away and read
   * it. The title is shown in the bar that holds the Close button.
   */
  pinnable?: { title: string };
};

export default function HeaderDetailTooltip({
  tooltip,
  placement,
  disableTooltip,
  sx,
  pinnable,
  children
}: HeaderDetailTooltipProps) {
  const { palette, shadows } = useTheme();
  const [hoverOpen, setHoverOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  const hoverSx = {
    "&:hover": {
      borderRadius: "4px",
      background: !disableTooltip
        ? colord(palette.patientHeader.main).darken(0.03).toHex()
        : undefined
    }
  };

  const unpin = () => {
    setPinned(false);
    setHoverOpen(false);
  };

  const pinnableProps = pinnable
    ? {
        open: !disableTooltip && (pinned || hoverOpen),
        onOpen: () => setHoverOpen(true),
        onClose: (event: Event | React.SyntheticEvent) => {
          setHoverOpen(false);
          if (event.type === "keydown") setPinned(false);
        }
      }
    : {};

  const content = pinnable ? (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 1.5, minHeight: 32 }}
      >
        <Typography variant="subtitle1" fontWeight="fontWeightSemiBold">
          {pinnable.title}
        </Typography>
        {pinned ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloseIcon />}
            onClick={unpin}
          >
            Close
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Click to keep open
          </Typography>
        )}
      </Stack>
      {tooltip}
    </>
  ) : (
    tooltip
  );

  return (
    <Tooltip
      componentsProps={{
        tooltip: {
          sx: {
            color: "common.black",
            bgcolor: "common.white",
            opacity: 1,
            boxShadow: shadows[3],
            minWidth: "450px",
            borderRadius: "12px",
            ...sx
          }
        }
      }}
      disableFocusListener
      placement={placement}
      title={disableTooltip ? null : <Box sx={{ p: 2 }}>{content}</Box>}
      PopperProps={{
        sx: {
          zIndex: 10
        }
      }}
      {...pinnableProps}
    >
      <Box
        sx={{ cursor: "default", ...hoverSx }}
        onClick={
          pinnable && !disableTooltip ? () => setPinned(true) : undefined
        }
      >
        {children}
      </Box>
    </Tooltip>
  );
}
